

import React, { Fragment, useEffect, useState, useMemo } from "react";
import { CardBody, Col, Row, Table, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Card, CardHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import moment from "moment";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../../assets/images/logos/logo_preta.jpeg'; 
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';

// Função para formatar data no padrão pt-BR
const formatarData = (dataString) => {
  if (!dataString) return '--';
  try {
    return moment(dataString).format('DD/MM/YYYY HH:mm');
  } catch (e) {
    return '--';
  }
};

// Função para formatar valores monetários no padrão real brasileiro
const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return 'R$ --';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(valor);
};


const ExportacaoExpositor = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  filters = [],
  statusOptions = [],
  nomeRelatorio,
  getStatus,
  feirasOptions = [], // Adicionando a função getStatus como prop
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isExportacaoAvancadaOpen, setIsExportacaoAvancadaOpen] = useState(false);
  const [camposSelecionados, setCamposSelecionados] = useState([]);
  const [ordenacao, setOrdenacao] = useState('atualizado_em');
  const [ordemDescendente, setOrdemDescendente] = useState(true);
  const camposDisponiveis = useMemo(() => {
    return columns
      .filter(col => col.header && col.accessorKey && !col.header.includes('Ação'))
      .map(col => {
        let accessor;
        let formatter = (value) => value;
        
        // Formatação para datas
        if (['reserva_valida_ate', 'created_at', 'updated_at'].includes(col.accessorKey)) {
          formatter = formatarData;
        }
        // Formatação para valores monetários
        else if (col.accessorKey.toLowerCase().includes('valor')) {
          formatter = formatarMoeda;
        }
        // Caso especial para coluna de status
        else if (col.accessorKey === 'etapas' && getStatus) {
          formatter = (item) => {
            
            // Verifica se é um objeto completo (para exportação)
            if (item && typeof item === 'object') {
              return getStatus(item.status_reserva_id, item.cancelado) || '--';
            }
            // Caso seja apenas o valor direto (para exibição na tabela)
            return item || '--';
          };
        }
        else if (col.accessorKey === 'etapas') {
          formatter = (item) => {
            
            if (!item || !Array.isArray(item)) return '--';
            const ultimaEtapa = item[item.length - 1]?.etapa_reserva?.nome;
            return ultimaEtapa || '--';
          };
        }

        if (col.accessorKey.includes('.')) {
          accessor = (item) => {
            const keys = col.accessorKey.split('.');
            const value = keys.reduce((obj, key) => obj?.[key] || '--', item);
            return formatter(value);
          };
        } else {
          accessor = (item) => {
            // Caso especial para status que precisa do item completo
            if (col.accessorKey === 'etapas' && getStatus) {
              return formatter(item);
            }

            return formatter(item[col.accessorKey] || '--');
          };
        }

        return {
          id: col.accessorKey,
          label: col.header,
          accessor: accessor
        };
      });
  }, [columns, getStatus]);

  // Inicializa os campos selecionados com as colunas visíveis
  useEffect(() => {
    if (camposDisponiveis.length > 0 && camposSelecionados.length === 0) {
      // Seleciona apenas as colunas visíveis por padrão (excluindo colunas como 'Ação')
      const defaultSelected = camposDisponiveis
        .filter(campo => !['Ação'].includes(campo.label))
        .map(campo => campo.id)
        .slice(0, 6); // Limita a 6 colunas por padrão
      setCamposSelecionados(defaultSelected);
    }
  }, [camposDisponiveis]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((acc, part) => {
        if (acc == null) return '';
        return acc[part];
      }, obj);
    };

    const cellValue = columnId.includes('.') 
      ? getNestedValue(row.original, columnId)
      : row.getValue(columnId);

    const stringValue = String(cellValue || '');
    
    const itemRank = rankItem(stringValue, value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const dateRangeFilter = (row, columnId, value) => {
    if (!value) return true;
    
    const cellDate = moment(row.getValue(columnId));
    const startDate = moment(value[0]).startOf('day');
    const endDate = moment(value[1]).endOf('day');
    
    return cellDate.isBetween(startDate, endDate, null, '[]');
  };

  const handleFeiraFilter = (feiraNome) => {
    const feiraColumn = table.getColumn('feira_nome'); // Usar o ID definido
    if (feiraColumn) {
      feiraColumn.setFilterValue(feiraNome || '');
    }
  };

    // Adicione esta função junto com as outras funções de filtro
  const exactFilter = (row, columnId, value) => {
    if (!value) return true;
    
    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((acc, part) => {
        if (acc == null) return '';
        return acc[part];
      }, obj);
    };

    const cellValue = columnId.includes('.') 
      ? getNestedValue(row.original, columnId)
      : row.getValue(columnId);

    return String(cellValue || '') === value;
  };

  // Adicione esta função auxiliar dentro do componente ExportacaoExpositor
const renderPaginationItems = () => {
    const pageCount = getPageOptions().length;
    const currentPage = getState().pagination.pageIndex;
    const maxVisible = 5; // Número máximo de botões de página visíveis
    
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pageCount, start + maxVisible);

    if (end - start < maxVisible) {
        start = Math.max(0, end - maxVisible);
    }

    const items = [];
    for (let i = start; i < end; i++) {
        items.push(
            <li key={i} className="page-item">
                <Link
                    to="#"
                    className={currentPage === i ? "page-link active" : "page-link"}
                    onClick={() => setPageIndex(i)}
                >
                    {i + 1}
                </Link>
            </li>
        );
    }
    return items;
};

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
      dateRange: dateRangeFilter,
      exact: exactFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState
  } = table;

  useEffect(() => {
    customPageSize && setPageSize(customPageSize);
  }, [customPageSize, setPageSize]);

  const handleStatusFilter = (status) => {
    const statusColumn = columns.find(col => col.accessorKey === 'etapas');
    if (statusColumn) {
      table.getColumn('etapas').setFilterValue(status);
    }
  };

  const toggleCampo = (campoId) => {
    if (camposSelecionados.includes(campoId)) {
      setCamposSelecionados(camposSelecionados.filter(id => id !== campoId));
    } else {
      setCamposSelecionados([...camposSelecionados, campoId]);
    }
  };

  const ordenarDados = (dados) => {
    return [...dados].sort((a, b) => {
      let valorA, valorB;
      
      switch (ordenacao) {
        case 'atualizado_em':
          valorA = new Date(a.updated_at).getTime();
          valorB = new Date(b.updated_at).getTime();
          break;
        case 'estande.nome':
          valorA = a.estande?.nome || '';
          valorB = b.estande?.nome || '';
          break;
        case 'comercial.nome':
          valorA = a.comercial?.nome || '';
          valorB = b.comercial?.nome || '';
          break;
        default:
          // Tenta ordenar pelo campo selecionado
          if (ordenacao.includes('.')) {
            const keys = ordenacao.split('.');
            valorA = keys.reduce((obj, key) => obj?.[key] || '', a);
            valorB = keys.reduce((obj, key) => obj?.[key] || '', b);
          } else {
            valorA = a[ordenacao] || '';
            valorB = b[ordenacao] || '';
          }
      }
      
      if (typeof valorA === 'string') {
        return ordemDescendente 
          ? valorB.localeCompare(valorA)
          : valorA.localeCompare(valorB);
      } else {
        return ordemDescendente ? valorB - valorA : valorA - valorB;
      }
    });
  };

  const gerarPDF = async () => {
    const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
    
    if (!filteredData || filteredData.length === 0) {
      toast.error('Nenhum dado disponível para exportar');
      return;
    }

    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const margin = 5;
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [54, 54, 54 ];

    // Cabeçalho
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(16);
    doc.text(nomeRelatorio, pageWidth / 2, 15, { align: 'center' });
    
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(margin, 25, pageWidth - margin, 25);

    // Configuração da tabela
    const headers = [
      "Expositor",
      "Status",
      "CNPJ",
      "Endereço",
      "Contatos",
      "Criado em"
    ];

    const rows = filteredData.map(item => {
      // Formata o status
      let status = '';
      if (item.pendente) status = 'Pendente';
      else if (item.aprovado) status = 'Aprovado';
      else if (item.aprovado === false) status = 'Reprovado';
      else if (item.ativo) status = 'Ativo';
      else status = 'Inativo';

      // Formata o endereço
      const endereco = [
        item.endereco,
        item.numero,
        item.complemento,
        item.bairro,
        `${item.cidade || ''}${item.estado ? `/${item.estado}` : ''}`,
        item.cep
      ].filter(Boolean).join(', ');

      // Formata os contatos
      const contatos = [
        item.telefone,
        item.celular,
        item.whatsapp && `WhatsApp: ${item.whatsapp}`,
        item.email
      ].filter(Boolean).join('\n');

      return [
        // Expositor com logo (se existir)
        {
          content: item.nome,
          styles: { fontStyle: 'bold' }
        },
        status,
        item.cnpj_codigo_internacional,
        endereco,
        contatos,
        moment(item.created_at).format('DD/MM/YYYY')
      ];
    });

    // Adiciona a tabela
    autoTable(doc, {
      startY: 30,
      head: [headers],
      body: rows,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontSize: 9,
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1
      },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold' },
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 50 },
        4: { cellWidth: 40 },
        5: { cellWidth: 20 }
      },
      styles: {
        overflow: 'linebreak',
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      didDrawCell: (data) => {
        // Adiciona a logo na primeira coluna se existir
        if (data.column.index === 0 && data.row.raw[0].image) {
          doc.addImage(
            data.row.raw[0].image, 
            'JPEG', 
            data.cell.x + 2, 
            data.cell.y + 2, 
            15, 
            15
          );
        }
      }
    });

    // Rodapé com logo
    doc.setFontSize(8);
    doc.setTextColor(100);
    
    // Data à esquerda
    doc.text(`Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`, margin, 285, { align: 'left' });
    
    // Logo no centro
    try {
      const logoWidth = 20;
      const logoHeight = 8;
      const logoX = (pageWidth - logoWidth) / 2;
      
      const img = new Image();
      img.src = logo;
      
      await new Promise((resolve) => {
        img.onload = () => {
          doc.addImage(img, 'JPEG', logoX, 280, logoWidth, logoHeight);
          resolve();
        };
        img.onerror = () => resolve();
      });
    } catch (e) {
      console.log('Erro ao carregar logo:', e);
      doc.setFontSize(10);
      doc.text('Meu Sistema', pageWidth / 2, 284, { align: 'center' });
    }
    
    // Número da página à direita
    doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - margin, 285, { align: 'right' });

    doc.save(`${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.pdf`);
    setIsExportacaoAvancadaOpen(false);
    toast.success('PDF gerado com sucesso!');
  };

  const exportarCSV = () => {
    try {
      const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
      
      if (!filteredData || filteredData.length === 0) {
        toast.error("Nenhum dado disponível para exportar");
        return;
      }

      // Cabeçalhos
      const headers = camposDisponiveis
        .filter(campo => camposSelecionados.includes(campo.id))
        .map(campo => campo.label);
      
      // Linhas de dados
      const rows = filteredData.map(item => {
        return camposDisponiveis
          .filter(campo => camposSelecionados.includes(campo.id))
          .map(campo => {
            const value = campo.accessor(item);
            // Trata valores que podem conter vírgulas
            return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
          });
      });

      // Cria conteúdo CSV
      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.join(';'))
      ].join('\n');

      // Cria blob e faz download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Falha ao exportar CSV');
    }
  };

  const exportarXML = () => {
    try {
      const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
      
      if (!filteredData || filteredData.length === 0) {
        toast.error("Nenhum dado disponível para exportar");
        return;
      }

      // Cria estrutura XML
      let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
      xmlContent += `<${nomeRelatorio.replace(/\s+/g, '_')}>\n`;
      
      filteredData.forEach(item => {
        xmlContent += '  <registro>\n';
        
        camposDisponiveis
          .filter(campo => camposSelecionados.includes(campo.id))
          .forEach(campo => {
            const value = campo.accessor(item);
            const tagName = campo.label.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
            xmlContent += `    <${tagName}>${escapeXml(value)}</${tagName}>\n`;
          });
        
        xmlContent += '  </registro>\n';
      });
      
      xmlContent += `</${nomeRelatorio.replace(/\s+/g, '_')}>`;

      // Cria blob e faz download
      const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${nomeRelatorio}_${moment().format('YYYYMMDD_HHmm')}.xml`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('XML exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar XML:', error);
      toast.error('Falha ao exportar XML');
    }
  };

  // Função auxiliar para escapar caracteres XML
  const escapeXml = (unsafe) => {
    if (unsafe === null || unsafe === undefined) return '';
    return unsafe.toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  return (
    <Fragment>
      <ToastContainer closeButton={false} />

      {isGlobalFilter && (
        <Row className="mb-1">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <Row className="g-3">
              <Col md={4}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <Input
                    value={globalFilter ?? ''}
                    onChange={e => setGlobalFilter(e.target.value)}
                    placeholder={SearchPlaceholder || "Pesquisar..."}
                    className="form-control"
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>

               {/* Filtro por Feira */}
              {feirasOptions.length > 0 && (
                <Col md={2}>
                  <Input
                    type="select"
                    onChange={(e) => handleFeiraFilter(e.target.value)}
                  >
                    <option value="">Todas as feiras</option>
                    {feirasOptions.map((feira, index) => (
                      <option key={index} value={feira}>{feira}</option>
                    ))}
                  </Input>
                </Col>
              )}

              {filters.some(f => f.column === 'etapas') && (
                <Col md={3}>
                  <Input
                    type="select"
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="">Todos os status</option>
                    {filters.find(f => f.column === 'etapas')?.options.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </Input>
                </Col>
              )}

              {filters.some(f => f.column === 'etapas_reserva') && (
                <Col md={3}>
                  <Input
                    type="select"
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="">Todos os status</option>
                    {filters.find(f => f.column === 'etapas_reserva')?.options.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </Input>
                </Col>
              )}

              <Col md={3} className="align-items-end">
                <Button 
                  color="primary" 
                  onClick={() => setIsExportacaoAvancadaOpen(true)}
                  disabled={!data || data.length === 0}>
                  <i className="ri-file-pdf-line align-bottom me-1"></i> Exportar
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Row>
      )}

      <div className="table-responsive px-3">
        <Table hover className={tableClass} style={{ width: 'calc(100% - 30px)' }}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={thClass} {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted()] ?? null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Paginação */}
      {/* Paginação Otimizada */}
<Row className="align-items-center mt-4 gy-3">
    <Col sm className="text-center text-sm-start">
        <div className="text-muted">
            Mostrando <span className="fw-semibold">{getRowModel().rows.length}</span> de{' '}
            <span className="fw-semibold">{data.length}</span> Resultados
        </div>
    </Col>
    <Col sm="auto">
        <ul className="pagination pagination-separated mb-0 justify-content-center flex-wrap">
            <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
                <Button 
                    color="none" 
                    className="page-link" 
                    onClick={previousPage}
                    disabled={!getCanPreviousPage()}
                >
                    Anterior
                </Button>
            </li>
            
            {/* Renderiza apenas o range de páginas calculado */}
            {renderPaginationItems()}

            <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
                <Button 
                    color="none" 
                    className="page-link" 
                    onClick={nextPage}
                    disabled={!getCanNextPage()}
                >
                    Próximo
                </Button>
            </li>
        </ul>
    </Col>
</Row>

      {/* Modal de Exportação Avançada */}
      <Modal isOpen={isExportacaoAvancadaOpen} toggle={() => setIsExportacaoAvancadaOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsExportacaoAvancadaOpen(false)}>Exportação Avançada</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Selecione os Campos</h5>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    {camposDisponiveis.map(campo => (
                      <div key={campo.id} className="form-check mb-2">
                        <Input
                          type="checkbox"
                          id={`campo-${campo.id}`}
                          checked={camposSelecionados.includes(campo.id)}
                          onChange={() => toggleCampo(campo.id)}
                        />
                        <Label for={`campo-${campo.id}`} check>
                          {campo.label}
                        </Label>
                      </div>
                    ))}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Opções de Ordenação</h5>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label for="ordenacao">Ordenar por:</Label>
                    <Input
                      type="select"
                      id="ordenacao"
                      value={ordenacao}
                      onChange={(e) => setOrdenacao(e.target.value)}
                    >
                      {camposDisponiveis.map(campo => (
                        <option key={campo.id} value={campo.id}>{campo.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                  
                  <FormGroup>
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id="ordemDescendente"
                        checked={ordemDescendente}
                        onChange={() => setOrdemDescendente(!ordemDescendente)}
                      />
                      <Label for="ordemDescendente" check>
                        Ordem Decrescente
                      </Label>
                    </div>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsExportacaoAvancadaOpen(false)}>
            Cancelar
          </Button>
          <div className="d-flex gap-2">
            <Button color="primary" onClick={gerarPDF}>
              <i className="ri-file-pdf-line align-bottom me-1"></i> PDF
            </Button>
            <Button color="success" onClick={exportarCSV}>
              <i className="ri-file-excel-line align-bottom me-1"></i> CSV
            </Button>
            <Button color="info" onClick={exportarXML}>
              <i className="ri-code-line align-bottom me-1"></i> XML
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ExportacaoExpositor;
